import { Fragment, useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { subscriptionsService } from "../../features/subscriptions/service";
import ConfirmModal from "../../components/admin/ConfirmModal";
import Pagination from "../../components/admin/Pagination";
import RefreshButton from "../../components/admin/RefreshButton";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import type {
  SubscriptionBlock,
  BillingEvent,
  SubscriptionTier,
  ConfigEntry,
  SubscriberRow,
  QuotaOverview,
} from "../../features/subscriptions/types";
import { parseApiError } from "../../lib/http/apiErrors";
import { formatISTMonthDayYear, formatISTDateTime } from "../../utils/dateUtils";
import { useAdminToast } from "../../context/AdminToastContext";
import { formatSeconds } from "../../features/subscriptions/planLimitsModel";

// ── Static tier definitions ───────────────────────────────────────────────────

const TIER_COLORS: Record<string, string> = {
  free: "bg-gray-700 text-gray-300",
  starter: "bg-blue-900 text-blue-300",
  mid: "bg-indigo-900 text-indigo-300",
  premium: "bg-purple-900 text-purple-300",
  pro: "bg-teal-900 text-teal-300",
  studio: "bg-cyan-900 text-cyan-300",
  elite: "bg-amber-900 text-amber-300",
};

const ALL_TIERS: SubscriptionTier[] = ["free","starter","mid","premium","pro","studio","elite"];
const ROLE_TIER_MAP: Record<string, SubscriptionTier[]> = {
  seller: ["free", "starter", "mid", "premium"],
  buyer: ["free", "pro", "studio", "elite"],
  partner: ["free"],
};

function TierBadge({ tier }: { tier: string }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${TIER_COLORS[tier] ?? "bg-gray-700 text-gray-300"}`}>
      {tier}
    </span>
  );
}

// Quota value semantics: -1 = unlimited, 0 = blocked, >0 = the cap.
function describeLimitValue(val: number | null): string {
  if (val === null) return "—";
  // Never surface the raw -1 sentinel to a human — show the infinity symbol.
  if (val < 0) return "∞";
  if (val === 0) return "Blocked";
  return String(val);
}

/** Quota numbers for one action as text: video time is held in seconds and read as
 *  "1m 30s"; everything else is a plain count. */
function quotaNumber(action: string, n: number): string {
  return action === "video_seconds" ? formatSeconds(n) : String(n);
}

/** Admin-facing name for an action in the quota tables and forms. */
function quotaActionLabel(action: string): string {
  return action === "video_seconds" ? "video time" : action;
}

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return formatISTMonthDayYear(iso);
}

function formatMoney(paise: number | null, currency: string | null) {
  if (paise === null || paise === undefined) return "—";
  return `${currency ?? "INR"} ${(paise / 100).toFixed(2)}`;
}

function expiryBadge(iso: string | null) {
  if (!iso) return { label: formatDate(iso), cls: "text-gray-400" };
  const days = (new Date(iso).getTime() - Date.now()) / 86400000;
  if (days < 0) return { label: `${formatDate(iso)} (expired)`, cls: "text-red-400" };
  if (days <= 7) return { label: `${formatDate(iso)} (soon)`, cls: "text-amber-400" };
  return { label: formatDate(iso), cls: "text-gray-300" };
}

// ── Config Tab ────────────────────────────────────────────────────────────────
const ROLE_TABS = [
  { key: "seller", label: "Seller" },
  { key: "buyer",  label: "Buyer"  },
  { key: "system", label: "System" },
] as const;

const TIER_OPTS: Record<string, { key: string; label: string; badge: string }[]> = {
  seller: [
    { key: "free",    label: "Free",    badge: "bg-gray-700 text-gray-300"   },
    { key: "starter", label: "Starter", badge: "bg-blue-900 text-blue-300"   },
    { key: "mid",     label: "Mid",     badge: "bg-indigo-900 text-indigo-300"},
    { key: "premium", label: "Premium", badge: "bg-purple-900 text-purple-300"},
  ],
  buyer: [
    { key: "free",   label: "Free",   badge: "bg-gray-700 text-gray-300"  },
    { key: "pro",    label: "Pro",    badge: "bg-teal-900 text-teal-300"   },
    { key: "studio", label: "Studio", badge: "bg-cyan-900 text-cyan-300"   },
    { key: "elite",  label: "Elite",  badge: "bg-amber-900 text-amber-300" },
  ],
  system: [],
};

/** Settings edited elsewhere: prices on Billing → Plans & Prices, feature
 *  switches on their own pages. Hidden from the Plan Limits view. */
export function isPriceOrSwitchKey(key: string): boolean {
  return key.startsWith("PRICE_") || key.endsWith("_ENABLED");
}

/** The config editor. `keyFilter` narrows which keys it lists — Billing →
 *  Plan Limits shows usage limits only; the Subscriptions page shows all. */
export function ConfigTab({ keyFilter }: { keyFilter?: (key: string) => boolean } = {}) {
  const [configs, setConfigs] = useState<ConfigEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { showError, showSuccess } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  useEffect(() => { if (success) showSuccess(success); }, [success, showSuccess]);
  const [roleFilter, setRoleFilter] = useState<"seller" | "buyer" | "system">("seller");
  const [tierFilter, setTierFilter] = useState<string | null>("free");
  const [resetTarget, setResetTarget] = useState<string | null>(null);
  const [infoTarget, setInfoTarget] = useState<string | null>(null);
  const [descEdit, setDescEdit] = useState<Record<string, string>>({});
  const [descSaving, setDescSaving] = useState<string | null>(null);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await subscriptionsService.adminGetConfig();
      setConfigs(res.configs);
      const initEdit: Record<string, string> = {};
      res.configs.forEach((c) => { initEdit[c.key] = String(c.override_value ?? c.default_value ?? ""); });
      setEditValues(initEdit);
    } catch (err) { setError(parseApiError(err).message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  // Reset tier selection when switching roles
  const switchRole = (r: "seller" | "buyer" | "system") => {
    setRoleFilter(r);
    setTierFilter(r === "system" ? null : "free");
  };

  const handleSave = async (key: string) => {
    const val = parseInt(editValues[key] ?? "");
    if (isNaN(val) || val < -1) { setError("Enter -1 (unlimited), 0 (blocked), or a positive integer."); return; }
    setSaving(key); setError(null);
    try {
      await subscriptionsService.adminSetConfig(key, val);
      setSuccess(`${key} → ${describeLimitValue(val)}`);
      await fetchConfigs();
    } catch (err) { setError(parseApiError(err).message); }
    finally { setSaving(null); }
  };

  const isDirty = (cfg: ConfigEntry) => {
    const current = String(cfg.override_value ?? cfg.default_value ?? "");
    return (editValues[cfg.key] ?? "") !== current;
  };

  const dirtyKeys = configs.filter(isDirty).map((c) => c.key);

  const handleSaveAll = async () => {
    const toSave = configs.filter(isDirty);
    if (toSave.length === 0) return;
    const parsed: { key: string; val: number }[] = [];
    for (const cfg of toSave) {
      const val = parseInt(editValues[cfg.key] ?? "");
      if (isNaN(val) || val < -1) {
        setError(`${cfg.key}: enter -1 (unlimited), 0 (blocked), or a positive integer.`);
        return;
      }
      parsed.push({ key: cfg.key, val });
    }
    setSaving("__all__"); setError(null);
    try {
      const results = await Promise.allSettled(
        parsed.map(({ key, val }) => subscriptionsService.adminSetConfig(key, val))
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed > 0) {
        setError(`${failed} of ${parsed.length} configs failed to save.`);
      } else {
        setSuccess(`Saved ${parsed.length} config${parsed.length === 1 ? "" : "s"}.`);
      }
      await fetchConfigs();
    } catch (err) { setError(parseApiError(err).message); }
    finally { setSaving(null); }
  };

  const handleReset = async (key: string) => {
    setSaving(key); setError(null);
    try {
      await subscriptionsService.adminResetConfig(key);
      setSuccess(`${key} reset to default`);
      await fetchConfigs();
    } catch (err) { setError(parseApiError(err).message); }
    finally { setSaving(null); }
  };

  const handleSaveDescription = async (key: string) => {
    const desc = (descEdit[key] ?? "").trim();
    if (!desc) { setError("Description cannot be empty."); return; }
    setDescSaving(key); setError(null);
    try {
      await subscriptionsService.adminSetConfigDescription(key, desc);
      setSuccess(`${key} description updated`);
      await fetchConfigs();
      setInfoTarget(null);
    } catch (err) { setError(parseApiError(err).message); }
    finally { setDescSaving(null); }
  };

  const filtered = configs.filter((c) => {
    if (keyFilter && !keyFilter(c.key)) return false;
    // Derive role/tier from key name when backend hasn't returned them yet
    const role = c.role ?? (
      c.key.includes("_SELLER") ? "seller" :
      c.key.includes("_BUYER")  ? "buyer"  : "system"
    );
    const tier = c.tier ?? (
      c.key.endsWith("_STARTER") || c.key.includes("_STARTER_") ? "starter" :
      c.key.endsWith("_MID")     || c.key.includes("_MID_")     ? "mid"     :
      c.key.endsWith("_PREMIUM") || c.key.includes("_PREMIUM")  ? "premium" :
      c.key.endsWith("_PRO")     || c.key.includes("_PRO_")     ? "pro"     :
      c.key.endsWith("_STUDIO")  || c.key.includes("_STUDIO_")  ? "studio"  :
      c.key.endsWith("_ELITE")   || c.key.includes("_ELITE")    ? "elite"   :
      "free"
    );
    if (role !== roleFilter) return false;
    if (tierFilter !== null && tier !== tierFilter) return false;
    return true;
  });

  const tierOpts = TIER_OPTS[roleFilter] ?? [];
  const overrideCount = configs.filter((c) => c.override_value !== null).length;

  if (loading) return <div className="text-gray-400 text-sm py-4">Loading config…</div>;

  return (
    <>
    <div className="space-y-4">
      {error && <div className="px-4 py-3 bg-red-900/40 border border-red-700 rounded-xl text-red-300 text-sm">{error}</div>}
      {success && <div className="px-4 py-3 bg-green-900/40 border border-green-700 rounded-xl text-green-300 text-sm">{success}</div>}

      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Overrides take effect within 5 min · shown in amber · <span className="text-red-300">0 = blocked</span>, <span className="text-green-300">-1 = unlimited</span>, click <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-gray-700 text-gray-300 text-[9px] font-bold">i</span> for help
          {overrideCount > 0 && <span className="ml-2 px-1.5 py-0.5 bg-amber-900/40 border border-amber-700 rounded text-amber-300">{overrideCount} active</span>}
        </p>
        <div className="flex items-center gap-3">
          <RefreshButton onRefresh={fetchConfigs} />
          <button onClick={handleSaveAll} disabled={dirtyKeys.length === 0 || saving !== null}
            className="px-3 py-1.5 bg-green-600 hover:bg-green-500 rounded-lg text-xs font-medium transition-colors disabled:opacity-40 whitespace-nowrap">
            {saving === "__all__" ? "Saving…" : `Save All${dirtyKeys.length > 0 ? ` (${dirtyKeys.length})` : ""}`}
          </button>
          {/* Role tabs */}
          <div className="flex bg-gray-900 rounded-xl p-1 gap-1">
            {ROLE_TABS.map((r) => (
              <button key={r.key} onClick={() => switchRole(r.key)}
                className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${roleFilter === r.key ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"}`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tier sub-tabs (hidden for system) */}
      {tierOpts.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {tierOpts.map((t) => (
            <button key={t.key} onClick={() => setTierFilter(t.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border-2 transition-colors ${
                tierFilter === t.key
                  ? "border-indigo-500 " + t.badge
                  : "border-gray-700 bg-gray-900 text-gray-500 hover:text-gray-300"
              }`}>
              <span className={`w-2 h-2 rounded-full ${t.badge.split(" ")[0]}`} />
              {t.label}
              {/* show override count per tier */}
              {(() => {
                const n = configs.filter((c) => {
                  const role = c.role ?? (c.key.includes("_SELLER") ? "seller" : c.key.includes("_BUYER") ? "buyer" : "system");
                  const tier = c.tier ?? (c.key.endsWith("_" + t.key.toUpperCase()) || c.key.includes("_" + t.key.toUpperCase() + "_") ? t.key : "free");
                  return role === roleFilter && tier === t.key && c.override_value !== null;
                }).length;
                return n > 0 ? <span className="ml-0.5 px-1 bg-amber-700/50 rounded text-amber-300">{n}</span> : null;
              })()}
            </button>
          ))}
        </div>
      )}

      <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
        <div className="divide-y divide-gray-800">
          {filtered.map((cfg) => {
            const isOverridden = cfg.override_value !== null;
            const effective = cfg.effective_value;
            const effectiveLabel = describeLimitValue(effective);
            return (
              <div key={cfg.key} className="px-5 py-3.5 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-xs font-mono truncate ${isOverridden ? "text-amber-300" : "text-gray-300"}`}>
                      {cfg.key}
                    </p>
                    <button
                      type="button"
                      title="What does this control?"
                      onClick={() => {
                        setInfoTarget(infoTarget === cfg.key ? null : cfg.key);
                        setDescEdit((p) => ({ ...p, [cfg.key]: p[cfg.key] ?? cfg.description }));
                      }}
                      className="w-4 h-4 flex items-center justify-center rounded-full bg-gray-700 hover:bg-indigo-600 text-gray-300 hover:text-white text-[10px] font-bold leading-none shrink-0"
                    >
                      i
                    </button>
                    {isOverridden && (
                      <span className="text-xs text-amber-400 font-medium bg-amber-900/30 px-1.5 py-0.5 rounded">overridden</span>
                    )}
                    {effective !== null && (
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                        effective < 0 ? "bg-green-900/30 text-green-300"
                        : effective === 0 ? "bg-red-900/30 text-red-300"
                        : "bg-gray-800 text-gray-400"
                      }`}>
                        {effectiveLabel}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Default: <span className="text-gray-500">{describeLimitValue(cfg.default_value)}</span>
                    {cfg.updated_by && <span className="ml-2">· {cfg.updated_by} · {formatDate(cfg.updated_at)}</span>}
                  </p>
                  {infoTarget === cfg.key && (
                    <div className="mt-2 p-3 bg-gray-950 border border-gray-800 rounded-xl space-y-2">
                      <p className="text-xs text-gray-400">{cfg.description}</p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={descEdit[cfg.key] ?? ""}
                          onChange={(e) => setDescEdit((p) => ({ ...p, [cfg.key]: e.target.value }))}
                          placeholder="Custom help text shown to admins…"
                          className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                        />
                        <button onClick={() => handleSaveDescription(cfg.key)} disabled={descSaving === cfg.key}
                          className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 whitespace-nowrap">
                          {descSaving === cfg.key ? "…" : "Save help text"}
                        </button>
                      </div>
                      {cfg.description_is_custom && (
                        <p className="text-[10px] text-gray-600">Custom description set by admin.</p>
                      )}
                    </div>
                  )}
                </div>
                <input type="number" min={-1} value={editValues[cfg.key] ?? ""}
                  onChange={(e) => setEditValues((p) => ({ ...p, [cfg.key]: e.target.value }))}
                  disabled={saving === "__all__"}
                  title="-1 = unlimited, 0 = blocked, >0 = cap"
                  className="w-24 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 disabled:opacity-50" />
                <button onClick={() => handleSave(cfg.key)} disabled={saving === cfg.key || saving === "__all__"}
                  className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 whitespace-nowrap">
                  {saving === cfg.key ? "…" : "Save"}
                </button>
                {isOverridden && (
                  <button onClick={() => setResetTarget(cfg.key)} disabled={saving === cfg.key || saving === "__all__"}
                    className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs transition-colors disabled:opacity-50">
                    Reset
                  </button>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="px-5 py-8 text-center text-gray-500 text-sm">No config keys for this selection.</div>
          )}
        </div>
      </div>
    </div>

    {resetTarget && (
      <ConfirmModal
        title="Reset Config Key"
        description={`Reset "${resetTarget}" back to its default value?`}
        items={["Override will be removed from database", "Quota will revert to the default limit"]}
        confirmLabel="Reset to Default"
        confirmClass="bg-amber-600 hover:bg-amber-500"
        onConfirm={() => { const k = resetTarget; setResetTarget(null); handleReset(k); }}
        onCancel={() => setResetTarget(null)}
      />
    )}
    </>
  );
}

// ── Payment Details Modal (full billing detail for one subscriber) ────────────
function formatDateTime(iso: string | null | undefined) {
  if (!iso) return "—";
  return formatISTDateTime(iso);
}

function eventTypeLabel(t: string) {
  return t.replace(/_/g, " ").replace(/\./g, " · ");
}

function ModeBadge({ mode }: { mode: string | null | undefined }) {
  if (!mode || mode === "unknown") return <span className="text-gray-600 text-xs">—</span>;
  const isTest = mode === "test";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
      isTest ? "bg-amber-900/40 text-amber-300 border border-amber-700" : "bg-green-900/40 text-green-300 border border-green-700"
    }`}>
      {mode}
    </span>
  );
}

function PaymentDetailsModal({
  userId, name, email, onClose, onManage,
}: {
  userId: string; name: string; email: string | null;
  onClose: () => void; onManage: () => void;
}) {
  useBodyScrollLock();
  const [block, setBlock] = useState<SubscriptionBlock | null>(null);
  const [events, setEvents] = useState<BillingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const [b, ev] = await Promise.all([
          subscriptionsService.adminGetUser(userId),
          subscriptionsService.adminGetUserEvents(userId, 100),
        ]);
        if (!cancelled) {
          setBlock(b);
          // Newest first, payments/renewals before bookkeeping noise of the same timestamp.
          setEvents([...ev.events].sort((a, c) => (c.created_at || "").localeCompare(a.created_at || "")));
        }
      } catch (err) {
        if (!cancelled) setError(parseApiError(err).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const paymentEvents = events.filter((e) => e.amount !== undefined && e.amount !== null);
  const otherEvents = events.filter((e) => !(e.amount !== undefined && e.amount !== null));

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-start justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold text-white">{name || "Unknown"}</h2>
            <p className="text-xs text-gray-500 font-mono">{email ?? userId}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors">✕</button>
        </div>

        <div className="p-6 space-y-6">
          {error && <div className="px-4 py-3 bg-red-900/40 border border-red-700 rounded-xl text-red-300 text-sm">{error}</div>}
          {loading ? (
            <div className="py-16 text-center text-gray-500">Loading…</div>
          ) : block ? (
            <>
              <div className="bg-gray-950 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center gap-2 flex-wrap mb-4">
                  <TierBadge tier={block.effective_tier ?? block.tier} />
                  <span className="text-sm text-gray-400 capitalize">{block.status.replace("_", " ")}</span>
                  <span className="text-xs text-gray-500 capitalize px-2 py-0.5 bg-gray-800 rounded-full">{block.gateway ?? "no gateway"}</span>
                  {block.admin_granted && (
                    <span className="text-xs bg-amber-900/40 border border-amber-700 text-amber-300 px-2 py-0.5 rounded-full">
                      Admin grant: {block.admin_granted_tier} until {formatDate(block.admin_granted_until)}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                  <div><p className="text-gray-500 text-xs">Started</p><p className="text-white">{formatDate(block.started_at)}</p></div>
                  <div><p className="text-gray-500 text-xs">Current period end</p><p className="text-white">{formatDate(block.current_period_end)}</p></div>
                  <div><p className="text-gray-500 text-xs">Next charge</p><p className="text-white">{formatDate(block.next_charge_at)}</p></div>
                  <div><p className="text-gray-500 text-xs">Auto-renew</p><p className="text-white">{block.cancel_at_period_end ? "Off (cancels at period end)" : "On"}</p></div>
                  <div><p className="text-gray-500 text-xs">Plan / Subscription ID</p><p className="text-white font-mono text-xs break-all">{block.plan_id ?? "—"}</p></div>
                  <div><p className="text-gray-500 text-xs">Gateway subscription ID</p><p className="text-white font-mono text-xs break-all">{block.gateway_subscription_id || block.razorpay_subscription_id || block.stripe_subscription_id || "—"}</p></div>
                  <div><p className="text-gray-500 text-xs">Last payment</p><p className="text-white">{formatDate(block.last_payment_at)}</p></div>
                  <div><p className="text-gray-500 text-xs">Last invoice amount</p><p className="text-white">{formatMoney(block.last_invoice_amount_paise, block.last_invoice_currency)}</p></div>
                  {block.cancelled_at && (
                    <div><p className="text-gray-500 text-xs">Cancelled</p><p className="text-white">{formatDate(block.cancelled_at)} {block.cancelled_reason ? `(${block.cancelled_reason})` : ""}</p></div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-white mb-3">
                  Payments {paymentEvents.length > 0 && <span className="text-gray-500 font-normal">({paymentEvents.length})</span>}
                </h3>
                {paymentEvents.length === 0 ? (
                  <p className="text-sm text-gray-500 px-1">No payment records stored for this user yet.</p>
                ) : (
                  <div className="bg-gray-950 border border-gray-800 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-800 text-gray-500">
                            <th className="text-left py-2.5 px-4 font-medium">Date</th>
                            <th className="text-left py-2.5 px-4 font-medium">Type</th>
                            <th className="text-left py-2.5 px-4 font-medium">Amount</th>
                            <th className="text-left py-2.5 px-4 font-medium">Payment ref</th>
                            <th className="text-left py-2.5 px-4 font-medium">Method</th>
                            <th className="text-left py-2.5 px-4 font-medium">Mode</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentEvents.map((ev) => (
                            <Fragment key={ev.event_id}>
                              <tr
                                className="border-b border-gray-800/50 hover:bg-gray-800/30 cursor-pointer transition-colors"
                                onClick={() => setExpanded(expanded === ev.event_id ? null : ev.event_id)}
                              >
                                <td className="py-2.5 px-4 text-gray-300 whitespace-nowrap">{formatDateTime(ev.created_at)}</td>
                                <td className="py-2.5 px-4 text-gray-300 capitalize">{eventTypeLabel(ev.event_type)}</td>
                                <td className="py-2.5 px-4 text-white">{formatMoney(ev.amount ?? null, ev.currency ?? "INR")}</td>
                                <td className="py-2.5 px-4 text-gray-400 font-mono text-xs">{ev.payment_id || ev.cf_payment_id || "—"}</td>
                                <td className="py-2.5 px-4 text-gray-400 capitalize">{ev.payment_method || "—"}</td>
                                <td className="py-2.5 px-4"><ModeBadge mode={ev.mode} /></td>
                              </tr>
                              {expanded === ev.event_id && (
                                <tr className="bg-gray-950/60 border-b border-gray-800/50">
                                  <td colSpan={6} className="px-4 py-3">
                                    <pre className="text-xs text-gray-400 whitespace-pre-wrap break-all font-mono">{JSON.stringify(ev, null, 2)}</pre>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {otherEvents.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-white mb-3">Subscription History <span className="text-gray-500 font-normal">({otherEvents.length})</span></h3>
                  <div className="space-y-1.5 max-h-56 overflow-y-auto">
                    {otherEvents.map((ev) => (
                      <div key={ev.event_id} className="flex items-center justify-between px-4 py-2.5 bg-gray-950 border border-gray-800 rounded-lg text-sm">
                        <span className="text-gray-300 capitalize">{eventTypeLabel(ev.event_type)}</span>
                        <span className="text-xs text-gray-500">{formatDateTime(ev.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>

        <div className="sticky bottom-0 bg-gray-900 border-t border-gray-800 px-6 py-4 flex justify-end">
          <button onClick={onManage} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium transition-colors">
            Manage subscription (grant / revoke / quota) →
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Subscribers Tab (list of everyone subscribed, billing, expiry) ────────────
function SubscribersTab({ onViewUser }: { onViewUser: (userId: string) => void }) {
  const [rows, setRows] = useState<SubscriberRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showError } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  const [page, setPage] = useState(0); // 0-indexed for <Pagination>
  const [pageSize, setPageSize] = useState(20);
  const [role, setRole] = useState<"" | "seller" | "buyer" | "partner">("");
  const [status, setStatus] = useState<"" | SubscriberRow["status"]>("");
  const [search, setSearch] = useState("");
  const [paidOnly, setPaidOnly] = useState(true);
  const [sortBy, setSortBy] = useState<"current_period_end" | "last_payment_at" | "started_at" | "next_charge_at">("current_period_end");
  const [sortOrder, setSortOrder] = useState<1 | -1>(1);
  const [detailRow, setDetailRow] = useState<SubscriberRow | null>(null);

  const fetchRows = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await subscriptionsService.adminListSubscribers({
        page: page + 1,
        page_size: pageSize,
        role: role || undefined,
        status: status || undefined,
        search: search.trim() || undefined,
        paid_only: paidOnly,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      setRows(res.items); setTotal(res.total);
    } catch (err) { setError(parseApiError(err).message); }
    finally { setLoading(false); }
  }, [page, pageSize, role, status, search, paidOnly, sortBy, sortOrder]);

  useEffect(() => {
    const t = setTimeout(fetchRows, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchRows, search]);

  const toggleSort = (key: typeof sortBy) => {
    if (sortBy === key) { setSortOrder(sortOrder === 1 ? -1 : 1); }
    else { setSortBy(key); setSortOrder(1); }
    setPage(0);
  };

  return (
    <div className="space-y-4">
      {error && <div className="px-4 py-3 bg-red-900/40 border border-red-700 rounded-xl text-red-300 text-sm">{error}</div>}

      <div className="flex items-center gap-3 flex-wrap">
        <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          placeholder="Search name, email, phone…"
          className="flex-1 min-w-[220px] px-4 py-2 bg-gray-900 border border-gray-800 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50" />
        <select value={role} onChange={(e) => { setRole(e.target.value as typeof role); setPage(0); }}
          className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-xl text-white text-sm focus:outline-none">
          <option value="">All roles</option>
          <option value="seller">Seller</option>
          <option value="buyer">Buyer</option>
          <option value="partner">Partner</option>
        </select>
        <select value={status} onChange={(e) => { setStatus(e.target.value as typeof status); setPage(0); }}
          className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-xl text-white text-sm focus:outline-none">
          <option value="">All statuses</option>
          <option value="trialing">Trialing</option>
          <option value="active">Active</option>
          <option value="past_due">Past due</option>
          <option value="cancelled">Cancelled</option>
          <option value="expired">Expired</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-400 px-1">
          <input type="checkbox" checked={paidOnly} onChange={(e) => { setPaidOnly(e.target.checked); setPage(0); }}
            className="w-4 h-4 rounded bg-gray-800 border-gray-700 text-indigo-500 focus:ring-indigo-500" />
          Paid only
        </label>
        <RefreshButton onRefresh={fetchRows} />
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-4 text-gray-500 font-medium">User</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Role</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Tier</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Status</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Gateway</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium cursor-pointer select-none" onClick={() => toggleSort("last_payment_at")}>
                  Last Payment {sortBy === "last_payment_at" ? (sortOrder === 1 ? "▲" : "▼") : ""}
                </th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium cursor-pointer select-none" onClick={() => toggleSort("current_period_end")}>
                  Expires {sortBy === "current_period_end" ? (sortOrder === 1 ? "▲" : "▼") : ""}
                </th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Auto-renew</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="py-16 text-center text-gray-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="py-16 text-center text-gray-500">No subscribers found</td></tr>
              ) : (
                rows.map((r) => {
                  const exp = expiryBadge(r.current_period_end);
                  return (
                    <tr key={r.user_id} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                      <td className="py-3 px-4">
                        <div className="text-white">{r.name}</div>
                        <div className="text-xs text-gray-500">{r.email ?? r.phone ?? "—"}</div>
                      </td>
                      <td className="py-3 px-4 text-gray-300 capitalize">{r.role ?? "—"}</td>
                      <td className="py-3 px-4">
                        <TierBadge tier={r.effective_tier} />
                        {r.admin_granted && <span className="ml-1.5 text-[10px] text-amber-400">admin</span>}
                      </td>
                      <td className="py-3 px-4 text-gray-300 capitalize">{r.status.replace("_", " ")}</td>
                      <td className="py-3 px-4 text-gray-400 capitalize">{r.gateway ?? "—"}</td>
                      <td className="py-3 px-4 text-gray-300">
                        <div>{formatDate(r.last_payment_at)}</div>
                        <div className="text-xs text-gray-500">{formatMoney(r.last_invoice_amount_paise, r.last_invoice_currency)}</div>
                      </td>
                      <td className={`py-3 px-4 ${exp.cls}`}>{exp.label}</td>
                      <td className="py-3 px-4 text-gray-300">{r.cancel_at_period_end ? "Off" : "On"}</td>
                      <td className="py-3 px-4 text-right">
                        <button onClick={() => setDetailRow(r)}
                          className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-200 transition-colors">
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination page={page} pageSize={pageSize} total={total}
        onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(0); }} variant="panel" />

      {detailRow && (
        <PaymentDetailsModal
          userId={detailRow.user_id}
          name={detailRow.name}
          email={detailRow.email}
          onClose={() => setDetailRow(null)}
          onManage={() => { const uid = detailRow.user_id; setDetailRow(null); onViewUser(uid); }}
        />
      )}
    </div>
  );
}

// ── Users Tab ─────────────────────────────────────────────────────────────────
function UsersTab({ initialUserId }: { initialUserId?: string | null }) {
  const [userId, setUserId] = useState("");
  const [searchResults, setSearchResults] = useState<{id: string, name: string, email: string}[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [lookupUserId, setLookupUserId] = useState("");
  const [subscription, setSubscription] = useState<SubscriptionBlock | null>(null);
  const [events, setEvents] = useState<BillingEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { showError, showSuccess } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  useEffect(() => { if (success) showSuccess(success); }, [success, showSuccess]);
  const [grantTier, setGrantTier] = useState<SubscriptionTier>("starter");
  const [grantDays, setGrantDays] = useState(30);
  const [grantReason, setGrantReason] = useState("");
  const [quotaAction, setQuotaAction] = useState("flyer");
  const [quotaUnit, setQuotaUnit] = useState<"sec" | "min">("sec");
  const [quotaCount, setQuotaCount] = useState(10);
  const [quotaReason, setQuotaReason] = useState("");
  // Per-action usage for this month. Fetched alongside the subscription so
  // an admin deciding whether to grant more can see what's actually been
  // used — the panel used to show only the grants, which told you nothing.
  const [overview, setOverview] = useState<QuotaOverview | null>(null);
  // A signed CHANGE, not the current value — so it starts neutral at 0.
  const [refundDelta, setRefundDelta] = useState(0);
  const [blockPhone, setBlockPhone] = useState("");

  useEffect(() => {
    if (userId.length < 2) {
      setSearchResults([]);
      return;
    }
    const delayDebounceFn = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await subscriptionsService.adminSearchUsers(userId);
        setSearchResults(res.users);
      } catch (err) {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [userId]);

  const fetchUser = useCallback(async (uid: string) => {
    if (!uid.trim()) return;
    setLoading(true); setError(null);
    try {
      const [sub, evts, ovw] = await Promise.all([
        subscriptionsService.adminGetUser(uid),
        subscriptionsService.adminGetUserEvents(uid, 100),
        // Non-fatal: a failed overview must not blank the whole lookup, since
        // the tier/grant controls below still work without it.
        subscriptionsService.adminQuotaOverview(uid).catch(() => null),
      ]);
      setSubscription(sub); setEvents(evts.events); setLookupUserId(sub.user_id || uid);
      setOverview(ovw);
      const validTiers = (ROLE_TIER_MAP[sub.role ?? "seller"] ?? ALL_TIERS).filter((t) => t !== "free");
      setGrantTier(validTiers[0] ?? "starter");
    } catch (err) {
      setError(parseApiError(err).message); setSubscription(null); setEvents([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (initialUserId) { setUserId(initialUserId); fetchUser(initialUserId); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUserId]);

  const act = async (fn: () => Promise<void>) => {
    setActionLoading(true); setError(null);
    try { await fn(); } catch (err) { setError(parseApiError(err).message); }
    finally { setActionLoading(false); }
  };

  return (
    <div className="space-y-6">
      {error && <div className="px-4 py-3 bg-red-900/40 border border-red-700 rounded-xl text-red-300 text-sm">{error}</div>}
      {success && <div className="px-4 py-3 bg-green-900/40 border border-green-700 rounded-xl text-green-300 text-sm">{success}</div>}

      <form onSubmit={(e) => { e.preventDefault(); fetchUser(userId); setShowDropdown(false); }} className="flex gap-3">
        <div className="relative flex-1">
          <input type="text" value={userId} 
            onChange={(e) => { setUserId(e.target.value); setShowDropdown(true); }}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
            placeholder="User ID, Email or Name…"
            className="w-full px-4 py-2.5 bg-gray-900 border border-gray-800 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50" />
          
          {showDropdown && userId.length >= 2 && (
            <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-700 rounded-xl shadow-lg overflow-hidden max-h-60 overflow-y-auto">
              {isSearching ? (
                <div className="px-4 py-3 text-sm text-gray-400">Searching...</div>
              ) : searchResults.length > 0 ? (
                searchResults.map(u => (
                  <button key={u.id} type="button"
                    onClick={() => {
                      setUserId(u.email || u.name || u.id);
                      setShowDropdown(false);
                      fetchUser(u.id);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-700 focus:bg-gray-700 focus:outline-none transition-colors border-b border-gray-700/50 last:border-0"
                  >
                    <div className="text-sm font-medium text-white">{u.name}</div>
                    <div className="text-xs text-gray-400 flex justify-between mt-0.5">
                       <span>{u.email}</span>
                       <span className="font-mono text-[10px] opacity-40">{u.id}</span>
                    </div>
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-sm text-gray-400">No users found</div>
              )}
            </div>
          )}
        </div>
        <button type="submit" disabled={loading}
          className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium disabled:opacity-50">
          {loading ? "Loading…" : "Look Up"}
        </button>
      </form>

      {subscription && lookupUserId && (
        <>
          <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
              <div className="min-w-0">
                <p className="text-xs text-gray-500 font-mono mb-2">{lookupUserId}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <TierBadge tier={subscription.effective_tier ?? subscription.tier} />
                  <span className="text-sm text-gray-400 capitalize">{subscription.status.replace("_", " ")}</span>
                  {subscription.admin_granted && (
                    <span className="text-xs bg-amber-900/40 border border-amber-700 text-amber-300 px-2 py-0.5 rounded-full">
                      Admin grant: {subscription.admin_granted_tier} until {formatDate(subscription.admin_granted_until)}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => act(async () => { await subscriptionsService.adminReconcileUser(lookupUserId); setSuccess("Synced."); await fetchUser(lookupUserId); })}
                  disabled={actionLoading} className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs disabled:opacity-50">
                  Sync Razorpay
                </button>
                {subscription.admin_granted && (
                  <button onClick={() => act(async () => { await subscriptionsService.adminRevoke(lookupUserId); setSuccess("Revoked."); await fetchUser(lookupUserId); })}
                    disabled={actionLoading} className="px-3 py-1.5 bg-red-900 hover:bg-red-800 rounded-lg text-xs text-red-300 disabled:opacity-50">
                    Revoke Grant
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div><p className="text-gray-500 text-xs">Period end</p><p className="text-white">{formatDate(subscription.current_period_end)}</p></div>
              <div><p className="text-gray-500 text-xs">Razorpay sub ID</p><p className="text-white font-mono text-xs truncate">{subscription.razorpay_subscription_id ?? "—"}</p></div>
              <div><p className="text-gray-500 text-xs">Last payment</p><p className="text-white">{formatDate(subscription.last_payment_at)}</p></div>
            </div>
          </div>

          <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
            <h2 className="text-sm font-semibold text-white mb-4">Admin Grant</h2>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Tier</label>
                <select value={grantTier} onChange={(e) => setGrantTier(e.target.value as SubscriptionTier)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50">
                  {(ROLE_TIER_MAP[subscription.role ?? "seller"] ?? ALL_TIERS)
                    .filter((t) => t !== "free")
                    .map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Days</label>
                <input type="number" min={1} max={365} value={grantDays} onChange={(e) => setGrantDays(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Reason</label>
                <input type="text" value={grantReason} onChange={(e) => setGrantReason(e.target.value)} maxLength={500} placeholder="e.g. partnership"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none" />
              </div>
            </div>
            <button onClick={() => act(async () => {
              if (!grantReason.trim()) { setError("Reason is required."); return; }
              await subscriptionsService.adminGrant(lookupUserId, { tier: grantTier, duration_days: grantDays, reason: grantReason });
              setSuccess(`Granted ${grantTier} for ${grantDays} days.`); setGrantReason("");
              await fetchUser(lookupUserId);
            })} disabled={actionLoading || !grantReason.trim()}
              className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium disabled:opacity-50">
              Grant Tier
            </button>
          </div>

          <div className={`rounded-2xl p-6 border ${subscription.unlimited_quota ? "bg-green-950 border-green-700" : "bg-gray-900 border-gray-800"}`}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-white mb-1">Unlimited Quota</h2>
                <p className="text-xs text-gray-500">Bypass all daily and monthly quota limits for this user.</p>
              </div>
              {subscription.unlimited_quota ? (
                <div className="flex items-center gap-3">
                  <span className="text-xs bg-green-800 border border-green-600 text-green-300 px-2.5 py-1 rounded-full font-semibold">Active</span>
                  <button onClick={() => act(async () => {
                    await subscriptionsService.adminRevokeUnlimitedQuota(lookupUserId);
                    setSuccess("Unlimited quota revoked.");
                    await fetchUser(lookupUserId);
                  })} disabled={actionLoading}
                    className="px-4 py-2 bg-red-900 hover:bg-red-800 rounded-xl text-xs font-medium text-red-300 disabled:opacity-50">
                    Revoke
                  </button>
                </div>
              ) : (
                <button onClick={() => act(async () => {
                  await subscriptionsService.adminGrantUnlimitedQuota(lookupUserId, "admin grant");
                  setSuccess("Unlimited quota granted.");
                  await fetchUser(lookupUserId);
                })} disabled={actionLoading}
                  className="px-4 py-2 bg-green-700 hover:bg-green-600 rounded-xl text-xs font-medium disabled:opacity-50">
                  Grant Unlimited
                </button>
              )}
            </div>
          </div>

          {overview && (
            <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
              <div className="flex items-baseline justify-between mb-1">
                <h2 className="text-sm font-semibold text-white">Quota Usage This Month</h2>
                <span className="text-xs text-gray-500 font-mono">{overview.period}</span>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                <span className="text-gray-400">Uploaded</span> = how many they actually did this
                month, unreduced. <span className="text-gray-400">Refunded</span> = how many came
                back — only deals can, by the seller deleting one they uploaded this month.{" "}
                <span className="text-gray-400">Counted</span> = Uploaded − Refunded, what actually
                counts against the cap. <span className="text-gray-400">Effective</span> = tier cap
                + admin extra, the number the gate enforces.{" "}
                <span className="text-gray-400">Remaining</span> = Effective − Counted.{" "}
                <span className="text-gray-400">∞</span> = uncapped.
                {overview.unlimited_quota && (
                  <span className="ml-1 text-green-400">This user has unlimited quota — every cap is bypassed.</span>
                )}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-800 text-gray-500">
                      <th className="text-left py-2 pr-3 font-medium">Action</th>
                      <th className="text-right py-2 px-3 font-medium">Uploaded</th>
                      <th className="text-right py-2 px-3 font-medium">Refunded</th>
                      <th className="text-right py-2 px-3 font-medium">Counted</th>
                      <th className="text-right py-2 px-3 font-medium">Tier cap</th>
                      <th className="text-right py-2 px-3 font-medium">Admin extra</th>
                      <th className="text-right py-2 px-3 font-medium">Effective</th>
                      <th className="text-right py-2 pl-3 font-medium">Remaining</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.actions.map((a) => {
                      const exhausted = a.remaining !== null && a.remaining <= 0;
                      return (
                        <tr key={a.action_type} className="border-b border-gray-800/60 hover:bg-gray-800/40">
                          <td className="py-2 pr-3 text-gray-300">{quotaActionLabel(a.action_type)}</td>
                          {/* Gross, deliberately NOT netted: an admin needs to see
                              that six uploads happened, even though two came back. */}
                          <td className="py-2 px-3 text-right text-white font-medium">
                            {quotaNumber(a.action_type, a.used + a.refunded)}
                          </td>
                          <td className="py-2 px-3 text-right">
                            {a.refunded > 0 ? (
                              <span
                                className="text-emerald-400"
                                title={`${a.refunded} returned by deleting deals uploaded this month`}
                              >
                                −{a.refunded}
                              </span>
                            ) : (
                              <span className="text-gray-600">—</span>
                            )}
                          </td>
                          {/* The net figure the quota gate actually compares. */}
                          <td className="py-2 px-3 text-right text-gray-200">{quotaNumber(a.action_type, a.used)}</td>
                          <td className="py-2 px-3 text-right text-gray-400">
                            {a.tier_limit === null ? "∞" : quotaNumber(a.action_type, a.tier_limit)}
                          </td>
                          <td className="py-2 px-3 text-right">
                            {a.admin_extra_unlimited ? (
                              <span className="text-green-400">∞</span>
                            ) : a.admin_extra === 0 ? (
                              <span className="text-gray-600">—</span>
                            ) : (
                              <span className={a.admin_extra > 0 ? "text-blue-300" : "text-amber-300"}>
                                {a.admin_extra > 0 ? `+${quotaNumber(a.action_type, a.admin_extra)}` : quotaNumber(a.action_type, a.admin_extra)}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-right text-gray-200">
                            {a.effective_limit === null ? "∞" : quotaNumber(a.action_type, a.effective_limit)}
                          </td>
                          <td className={`py-2 pl-3 text-right font-medium ${exhausted ? "text-red-400" : "text-gray-300"}`}>
                            {a.remaining === null ? "∞" : quotaNumber(a.action_type, a.remaining)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {overview.published_deals && (
                <div className="mt-4 pt-4 border-t border-gray-800 flex flex-wrap gap-x-8 gap-y-2 text-xs">
                  <span className="text-gray-500">
                    Published this month:{" "}
                    <span className="text-white font-medium">
                      {overview.published_deals.used} /{" "}
                      {overview.published_deals.limit === null ? "∞" : overview.published_deals.limit}
                    </span>
                  </span>
                  {overview.published_deals.previous_months_live > 0 && (
                    <span className="text-gray-500">
                      Still live from earlier months:{" "}
                      <span className="text-white font-medium">
                        {overview.published_deals.previous_months_live}
                      </span>
                    </span>
                  )}
                  {overview.deal_upload_refunds && (
                    <span className="text-gray-500">
                      Delete-refunds left:{" "}
                      <span className="text-white font-medium">
                        {overview.deal_upload_refunds.remaining === null
                          ? "∞"
                          : `${overview.deal_upload_refunds.remaining} of ${overview.deal_upload_refunds.limit}`}
                      </span>
                      {overview.deal_upload_refunds.used > 0 && (
                        <span className="ml-1 text-gray-600">({overview.deal_upload_refunds.used} taken)</span>
                      )}
                      {overview.deal_upload_refunds.blocked && (
                        <span className="ml-1.5 text-amber-400">(blocked)</span>
                      )}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {overview?.deal_upload_refunds && (
            <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
              <h2 className="text-sm font-semibold text-white mb-1">Delete-Refund Allowance</h2>
              <p className="text-xs text-gray-500 mb-4">
                When this seller deletes a deal they uploaded <span className="text-gray-400">the
                same month</span>, that upload is returned to their monthly allowance — so they can
                upload a replacement. This caps how many times a month that can happen, which is the
                brake on upload/delete looping. Blocking stops the refunds only; the seller can
                always still delete a deal.
              </p>
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Refunds left this month</label>
                  <p className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm">
                    {overview.deal_upload_refunds.remaining === null
                      ? "∞"
                      : `${overview.deal_upload_refunds.remaining} of ${overview.deal_upload_refunds.limit}`}
                    <span className="ml-1.5 text-xs text-gray-500">
                      ({overview.deal_upload_refunds.used} already taken)
                    </span>
                  </p>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Current cap</label>
                  <p className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm">
                    {overview.deal_upload_refunds.limit === null ? "∞" : overview.deal_upload_refunds.limit}
                    <span className="ml-1.5 text-xs text-gray-500">
                      ({overview.deal_upload_refunds.tier_limit ?? "?"} from tier
                      {(overview.deal_upload_refunds.adjustment ?? 0) !== 0
                        ? `, ${(overview.deal_upload_refunds.adjustment ?? 0) > 0 ? "+" : ""}${overview.deal_upload_refunds.adjustment} by admin`
                        : ""})
                    </span>
                  </p>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Change cap by (+/-)</label>
                  <input type="number" min={-1000} max={1000} value={refundDelta}
                    onChange={(e) => setRefundDelta(Number(e.target.value))}
                    placeholder="0"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none" />
                  <p className="mt-1 text-[11px] text-gray-600">
                    {refundDelta === 0
                      ? "No change"
                      : `Cap ${overview.deal_upload_refunds.limit ?? "∞"} → ${Math.max(0, (overview.deal_upload_refunds.limit ?? 0) + refundDelta)}`}
                  </p>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Reason</label>
                  <input type="text" value={quotaReason} onChange={(e) => setQuotaReason(e.target.value)} maxLength={500}
                    placeholder="e.g. trusted agency"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none" />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => act(async () => {
                  if (!quotaReason.trim()) { setError("Reason is required."); return; }
                  const r = await subscriptionsService.adminAdjustRefundAllowance(lookupUserId, refundDelta, quotaReason);
                  setSuccess(`Refund cap adjusted by ${r.delta > 0 ? "+" : ""}${r.delta} (now ${r.effective_refund_limit === null ? "∞" : r.effective_refund_limit}).`);
                  setQuotaReason(""); await fetchUser(lookupUserId);
                })} disabled={actionLoading || !quotaReason.trim() || refundDelta === 0}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-500 rounded-xl text-sm font-medium disabled:opacity-50">
                  {refundDelta > 0 ? `Increase cap by ${refundDelta}` : refundDelta < 0 ? `Reduce cap by ${Math.abs(refundDelta)}` : "Change cap"}
                </button>
                {overview.deal_upload_refunds.blocked ? (
                  <button onClick={() => act(async () => {
                    await subscriptionsService.adminSetRefundBlocked(lookupUserId, false);
                    setSuccess("Delete-refunds re-enabled."); await fetchUser(lookupUserId);
                  })} disabled={actionLoading}
                    className="px-5 py-2 bg-green-700 hover:bg-green-600 rounded-xl text-sm font-medium disabled:opacity-50">
                    Unblock Refunds
                  </button>
                ) : (
                  <button onClick={() => act(async () => {
                    if (!quotaReason.trim()) { setError("Reason is required to block."); return; }
                    await subscriptionsService.adminSetRefundBlocked(lookupUserId, true, quotaReason);
                    setSuccess("Delete-refunds blocked for this seller.");
                    setQuotaReason(""); await fetchUser(lookupUserId);
                  })} disabled={actionLoading || !quotaReason.trim()}
                    className="px-5 py-2 bg-red-900 hover:bg-red-800 rounded-xl text-sm font-medium text-red-300 disabled:opacity-50">
                    Block Refunds
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
            <h2 className="text-sm font-semibold text-white mb-1">Adjust Extra Quota</h2>
            <p className="text-xs text-gray-500 mb-4">
              Add or remove monthly uses for one action, without changing the user's tier.
              The value is a <span className="text-gray-400">signed adjustment</span> —
              <span className="text-gray-400"> +10</span> gives ten more this month,
              <span className="text-gray-400"> -3</span> takes three away. It's added to the
              tier cap, so a later tier change carries it along. For truly unlimited, use the
              Unlimited Quota control above.
            </p>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Action Type</label>
                <select value={quotaAction} onChange={(e) => setQuotaAction(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50">
                  {(overview?.actions.map((a) => a.action_type)
                    ?? ["flyer", "itinerary", "video", "connect", "enquiry", "chat_session", "market_research", "webinar", "deal"]
                  ).map((a) => (
                    <option key={a} value={a}>{a === "video_seconds" ? "video time (seconds)" : a}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  {quotaAction === "video_seconds" ? "Extra video time" : "Adjust by (+/-)"}
                </label>
                {quotaAction === "video_seconds" ? (
                  <>
                    <div className="flex gap-2">
                      <input type="number" min={0} step={quotaUnit === "min" ? 0.5 : 1}
                        value={quotaUnit === "min" ? Math.round((quotaCount / 60) * 100) / 100 : quotaCount}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          setQuotaCount(Math.round(quotaUnit === "min" ? n * 60 : n));
                        }}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none" />
                      <select value={quotaUnit} onChange={(e) => setQuotaUnit(e.target.value as "sec" | "min")}
                        className="px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-xs">
                        <option value="sec">seconds</option>
                        <option value="min">minutes</option>
                      </select>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1">
                      {quotaCount > 0 ? `Adds ${formatSeconds(quotaCount)} to this month's video time (max 2h 46m per grant).` : "Enter the time to add."}
                    </p>
                  </>
                ) : (
                  <input type="number" min={-10000} max={10000} value={quotaCount} onChange={(e) => setQuotaCount(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none" />
                )}
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Reason</label>
                <input type="text" value={quotaReason} onChange={(e) => setQuotaReason(e.target.value)} maxLength={500} placeholder="e.g. onboarding bonus"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none" />
              </div>
            </div>
            {subscription?.admin_extra_quota && Object.keys(subscription.admin_extra_quota).length > 0 && (
              <div className="mb-4 flex flex-wrap gap-2">
                {Object.entries(subscription.admin_extra_quota).filter(([, count]) => count !== 0).map(([action, count]) => (
                  <span key={action} className="inline-flex items-center gap-1.5 text-xs bg-blue-900/40 border border-blue-700 text-blue-300 pl-2 pr-1 py-0.5 rounded-full">
                    {count > 0 ? `+${quotaNumber(action, count)}` : quotaNumber(action, count)} {quotaActionLabel(action)}
                    <button
                      onClick={() => act(async () => {
                        await subscriptionsService.adminQuotaRevoke(lookupUserId, action);
                        setSuccess(`Removed extra quota grant for ${quotaActionLabel(action)}.`);
                        await fetchUser(lookupUserId);
                      })}
                      disabled={actionLoading}
                      title={`Remove extra quota grant for ${action}`}
                      className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-blue-800 text-blue-300 hover:text-white disabled:opacity-50 leading-none"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <button onClick={() => act(async () => {
              if (!quotaReason.trim()) { setError("Reason is required."); return; }
              if (quotaCount === 0) { setError("Enter a non-zero adjustment."); return; }
              if (quotaAction === "video_seconds" && (quotaCount < 1 || quotaCount > 10000)) {
                setError("Video time to add must be between 1 second and 10,000 seconds (2h 46m).");
                return;
              }
              const r = await subscriptionsService.adminQuotaGrant(lookupUserId, { action_type: quotaAction, extra_count: quotaCount, reason: quotaReason });
              const sign = r.extra_count_added > 0 ? "+" : "";
              setSuccess(`Adjusted ${quotaActionLabel(quotaAction)} by ${sign}${quotaNumber(quotaAction, r.extra_count_added)} (total extra: ${quotaNumber(quotaAction, r.total_admin_extra)}).`);
              setQuotaReason(""); await fetchUser(lookupUserId);
            })} disabled={actionLoading || !quotaReason.trim() || quotaCount === 0}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-500 rounded-xl text-sm font-medium disabled:opacity-50">
              Apply Adjustment
            </button>
          </div>

          {events.length > 0 && (
            <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
              <h2 className="text-sm font-semibold text-white mb-4">Billing History ({events.length})</h2>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {events.map((ev) => (
                  <div key={ev.event_id} className="flex items-center justify-between px-4 py-3 bg-gray-800 rounded-xl">
                    <div>
                      <p className="text-sm text-white">{ev.event_type.replace(/_/g, " ")}</p>
                      <p className="text-xs text-gray-500 font-mono">{ev.event_id}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-400">{formatDate(ev.created_at)}</p>
                      <span className={`text-xs ${ev.processed ? "text-green-400" : "text-yellow-400"}`}>{ev.processed ? "done" : "pending"}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
        <h2 className="text-sm font-semibold text-white mb-1">Trial Block / Unblock</h2>
        <p className="text-xs text-gray-500 mb-4">Block a phone from starting free trials (anti-fraud).</p>
        <div className="flex gap-3">
          <input type="text" value={blockPhone} onChange={(e) => setBlockPhone(e.target.value)} placeholder="+91xxxxxxxxxx"
            className="flex-1 px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none" />
          <button onClick={() => act(async () => { await subscriptionsService.adminBlockPhone(blockPhone.trim()); setSuccess(`${blockPhone} blocked.`); setBlockPhone(""); })}
            disabled={actionLoading || !blockPhone.trim()}
            className="px-4 py-2.5 bg-red-800 hover:bg-red-700 rounded-xl text-sm font-medium disabled:opacity-50">Block</button>
          <button onClick={() => act(async () => { await subscriptionsService.adminUnblockPhone(blockPhone.trim()); setSuccess(`${blockPhone} unblocked.`); setBlockPhone(""); })}
            disabled={actionLoading || !blockPhone.trim()}
            className="px-4 py-2.5 bg-gray-700 hover:bg-gray-600 rounded-xl text-sm font-medium disabled:opacity-50">Unblock</button>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function AdminSubscriptionsPage() {
  const [tab, setTab] = useState<"list" | "users" | "config">("list");
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { showError, showSuccess } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  useEffect(() => { if (success) showSuccess(success); }, [success, showSuccess]);
  const [reconcileConfirm, setReconcileConfirm] = useState(false);

  const handleReconcileAll = async () => {
    setActionLoading(true); setError(null);
    try { await subscriptionsService.adminReconcile(); setSuccess("Batch reconciliation triggered."); }
    catch (err) { setError(parseApiError(err).message); }
    finally { setActionLoading(false); }
  };

  const TABS = [
    { key: "list", label: "All Subscribers" },
    { key: "users", label: "User Lookup" },
    { key: "config", label: "All settings (advanced)" },
  ] as const;

  return (
    <>
    <div>
      <AdminPageHeader
        title="Subscriptions"
        subtitle="Manage user tiers, packages, billing, and quota limits"
        actions={
          <button onClick={() => setReconcileConfirm(true)} disabled={actionLoading}
            className="px-4 py-2 bg-indigo-700 hover:bg-indigo-600 rounded-xl text-sm font-medium transition-colors disabled:opacity-50">
            Reconcile All
          </button>
        }
      />

      {error && <div className="mb-4 px-4 py-3 bg-red-900/40 border border-red-700 rounded-xl text-red-300 text-sm">{error}</div>}
      {success && <div className="mb-4 px-4 py-3 bg-green-900/40 border border-green-700 rounded-xl text-green-300 text-sm">{success}</div>}

      <div className="flex flex-wrap gap-1 mb-8 bg-gray-900 rounded-xl p-1 w-fit max-w-full">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.key ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "list" && <SubscribersTab onViewUser={(uid) => { setPendingUserId(uid); setTab("users"); }} />}
      {tab === "users" && <UsersTab initialUserId={pendingUserId} />}
      {tab === "config" && <ConfigTab />}
    </div>

    {reconcileConfirm && (
      <ConfirmModal
        title="Batch Reconciliation"
        description="Run batch reconciliation against Razorpay for all users?"
        items={[
          "Sync subscription status with Razorpay payment records",
          "May update tier/status for users with mismatched data",
        ]}
        confirmLabel="Run Reconciliation"
        confirmClass="bg-indigo-600 hover:bg-indigo-500"
        onConfirm={() => { setReconcileConfirm(false); handleReconcileAll(); }}
        onCancel={() => setReconcileConfirm(false)}
      />
    )}
    </>
  );
}
